import { ExaminerFunction } from '../pages/Assessment/Assessment.types';
import { IPageInfo } from './page.types';

export enum Folder {
  Received = 1,
  Sent,
  Removed,
  SpecialCasesToSolve,
  SpecialCasesSolved,
  HelpdeskReported,
  HelpdeskSolved,
}

export const foldersWithSeparatedParticipant: readonly Folder[] = [Folder.Received, Folder.Sent];
export const foldersWithUnReadBold: readonly Folder[] = [
  Folder.Received,
  Folder.SpecialCasesToSolve,
  Folder.HelpdeskReported,
];
export const foldersWithForwardEnabled: readonly Folder[] = [
  Folder.Received,
  Folder.Sent,
  Folder.Removed,
  Folder.SpecialCasesToSolve,
];
export const foldersWithDeleteEnabled: readonly Folder[] = [Folder.Received, Folder.Sent];
export const foldersForHelpdesk: readonly Folder[] = [Folder.HelpdeskReported, Folder.HelpdeskSolved];
export const foldersWithGroupingDisabled: readonly Folder[] = [Folder.Removed];
export const foldersWithNeedToInsertSendMessage: readonly Folder[] = [
  Folder.Sent,
  Folder.SpecialCasesToSolve,
  Folder.SpecialCasesSolved,
  Folder.HelpdeskSolved,
  Folder.HelpdeskReported,
];
export const foldersWithAllParticantsIncludedInReply: readonly Folder[] = [
  Folder.SpecialCasesToSolve,
  Folder.SpecialCasesSolved,
];
export const foldersWithSpecialCaseDataDisplaying: readonly Folder[] = [
  Folder.SpecialCasesToSolve,
  Folder.SpecialCasesSolved,
];
export const foldersWithHelpdesk: readonly Folder[] = [Folder.HelpdeskReported, Folder.HelpdeskSolved];

export interface FoldersUnreadMessagesIndicator {
  folder: Folder;
  unreadMessages: number;
}

export interface FoldersUnreadMessagesIndicators {
  folderUnreadMessagesIndicators: FoldersUnreadMessagesIndicator[];
}

export interface FoldersSubFolder {
  id: Folder;
  icon: React.ReactElement;
  text: string;
}

export interface FolderWithSubFolders {
  name: string;
  items: FoldersSubFolder[];
}

export enum Priority {
  Low = 1,
  Medium,
  High,
}

export interface IRecipientList {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  examinerNumber?: string;
  accountType?: string;
  isVerifier?: boolean;
}

export interface FolderUnread {
  Folder: Folder;
  HasUnread: boolean;
}

export interface Recipient {
  userId: string;
  functionId: string;
  function: ExaminerFunction;
}

export interface MessageRecipient {
  userId: string;
  functionId: string;
  function: ExaminerFunction;
  firstName: string;
  lastName: string;
  displayName: string;
  inferiors?: MessageRecipientTeam[];
}

export interface RecipientTreeOption extends MessageRecipientTeam {
  isFirst?: boolean;
  parentIds?: string[];
  depth?: number;
  isLastSubordinate?: boolean;
  canCollapse?: boolean;
  hidden?: boolean;
  hasNeighbour?: boolean;
}

interface MessageRecipientTeam extends MessageRecipient {
  inferiors?: MessageRecipient[];
}

export interface MessageRecipients {
  team: MessageRecipientTeam[];
  peers: MessageRecipient[];
  supervisor: MessageRecipient[];
  examinerFunction: ExaminerFunction;
}
export interface IMessageRecipientsList {
  pageInfo: IPageInfo;
  userList: IRecipientList[];
}

export interface MessageParticipant {
  userId: string;
  deskId: string;
  function: ExaminerFunction | null;
  firstName: string;
  lastName: string;
  displayName: string;
  isVerifier?: boolean;
}

export interface Conversation {
  conversationMessages: ConversationMessage[];
  pageInfo: IPageInfo;
}

export interface Message {
  messageId: string;
  conversationId: string;
  assessmentSession: string;
  sender: MessageParticipant;
  recipient: MessageParticipant;
  subject: string;
  sentDate: string;
  priorityId: Priority;
  flag: FlagId | null;
  hasAttachment: boolean;
  hasUrl: boolean;
  isCurrentUserConversationInitiator: boolean;
}

export interface UserSigningInfoMessage {
  userId: string;
  signedIn: boolean;
}

export interface FolderMessagesResponse {
  filteredFolderMessages: FolderMessage[];
  folderUnreadMessagesIndicatorDto: FoldersUnreadMessagesIndicators | null;
  current: FolderMessageContent | null;
  conversation: Conversation | null;
  pageInfo: IPageInfo;
}

export interface ConversationMessagesResponse {
  conversationMessages: ConversationMessage[];
}

export interface FolderMessage extends Message {
  isGroup: boolean;
  unreadMessages: number;
  assessmentSessionId: string | null;
  examTaskGroupSolutionScoreId: string | null;
}

export interface ConversationMessage extends Message {
  isRead: boolean;
}

export interface FolderMessageContent {
  messageId: string;
  conversationId: string;
  assessmentSession: string;
  assessmentSessionId?: string;
  sender: MessageParticipant;
  recipient: MessageParticipant[];
  subject: string;
  body: string;
  sentDate: string;
  priorityId: Priority;
  flag: FlagId | null;
  isRead: boolean;
  attachmentName: string | null;
  attachmentSize: number | null;
  url: string;
  examTaskGroupSolutionScoreId: string | null;
  taskSolutionSheetId: string | null;
  specialCaseDefinitionTitle: string | null;
}

export enum FlagId {
  Green,
  Yellow,
  Red,
}

export enum MonitId {
  hasAttachment,
  hasLink,
  mediumPriority,
  highPriority,
}

export enum MessagesSortBy {
  Flag = 1,
  Author,
  Subject,
  AssessmentSession,
  Monit,
  SentDate,
}

export enum ConversationType {
  Standard,
  SpecialCase,
  HelpDesk,
}

export interface MessageInFolder {
  folder: Folder;
  message: Message;
}

export interface SpecialCaseSolvingSuccessResults {
  assessmentSessionId: string;
  examTaskGroupSolutionScoreId: string;
}

export interface FolderAndSubFolderCaptions {
  folder: string | null;
  subFolder: string | null;
}
